import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ShoppingCart, ExternalLink, ShoppingBag } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useImageErrorHandling } from "@/lib/image-utils";
import { useProductDetailModal } from "@/features/product-detail/hooks/use-product-detail-modal";
import { cn } from "@/lib/utils";
import { isModifiedClick } from "@/lib/link-utils";
import useAuthStore from "@/store/use-auth-store";
import { formatPrice } from "@/features/orders/utils/format-price";
import { useCommercePaths } from "@/modules";
import { FavoriteButton } from "@/features/favorites";
import { getProductVariantLabel } from "./product-variant-label";

interface ProductCardProps {
  product: any;
}

export function ProductCard({ product }: ProductCardProps) {
  const { t } = useTranslation();
  const paths = useCommercePaths();
  const { openModal } = useProductDetailModal();
  const { handleImageError, isImageFailed } = useImageErrorHandling();
  const user = useAuthStore((state) => state.user);
  const userCurrency =
    user?.currency ||
    "TRY";

  // 🆕 Ürün verilerini parse et (Yeni Typesense yapısı)
  const productName =
    product.displayName ||
    product.name ||
    product.title ||
    product.productName ||
    t("instantSearch.product");

  // 🆕 Fiyat bilgileri (prices object'inden)
  const effectivePrice =
    product.effectivePrice ||
    product.amount ||
    0;
  const listPrice = product.prices?.listPrice?.value || product.amountOld || 0;
  const productPrice = formatPrice(effectivePrice, userCurrency);
  const productOldPrice = listPrice > effectivePrice ? listPrice : null;

  // 🆕 İndirim oranını hesapla
  const indirimOran =
    productOldPrice && effectivePrice > 0
      ? Math.round(((productOldPrice - effectivePrice) / productOldPrice) * 100)
      : null;

  // 🆕 Görsel bilgisi (images array'inden ilk görseli al)
  const productImage =
    product.imageUrl || // Ana görsel
    product.images?.[0]?.imageUrl || // İlk görsel
    product.imagePaths?.[0]?.imagePath || // Eski format
    product.image_url ||
    product.image ||
    "";

  // 🆕 URL bilgileri
  const productUrl =
    product.canonicalUrl ||
    product.sourceUrl ||
    product.productUrl ||
    product.url ||
    product.link;

  // 🆕 Brand bilgisi
  const brandName =
    product.brand?.name ||
    product.merchant?.name ||
    product.seller?.name ||
    product.brand_logo;
  const brandLogo = product.seller?.logoUrl || product.brand?.logoUrl;
  const variantLabel = getProductVariantLabel(product);

  // Product ID'yi al (Typesense format)
  const productId = product.productId;
  const offerId =
    typeof product.offerId === "string" && product.offerId.trim()
      ? product.offerId.trim()
      : null;
  const productDetailUrl = productId
    ? paths.product(productId, { offerId: offerId ?? undefined })
    : null;

  // Kart kendi genişliğine göre ölçeklenir (@min-[200px]/card): dock'ta
  // 170px'lik kart sıkı, tam sayfada 260px'lik kart ferah — pencere fark etmez
  const cardClassName =
    "@container/card flex h-full w-full flex-col border border-gray-200 dark:border-gray-700 rounded-xl @min-[200px]/card:rounded-2xl p-1.5 @min-[200px]/card:p-2 group cursor-pointer hover:shadow-lg transition-shadow bg-white dark:bg-gray-800";

  const cardContent = (
    <>
      {/* Product Image — kare alan, tüm kartlarda aynı oran */}
      <div className="relative aspect-square shrink-0 rounded-lg @min-[200px]/card:rounded-xl overflow-hidden mb-1.5 @min-[200px]/card:mb-3 bg-gray-100 dark:bg-gray-800">
        {productImage && !isImageFailed(productImage) ? (
          <img
            src={productImage}
            alt={productName}
            onError={() => handleImageError(productImage)}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-muted-foreground">
            <ShoppingBag className="h-8 w-8 @min-[200px]/card:h-12 @min-[200px]/card:w-12" />
          </div>
        )}

        {/* Discount Badge */}
        {indirimOran && indirimOran > 0 && (
          <div className="absolute top-1.5 left-1.5 @min-[200px]/card:top-2 @min-[200px]/card:left-2 bg-red-500 text-white px-1.5 py-0.5 @min-[200px]/card:px-2 @min-[200px]/card:py-1 rounded-md text-[10px] @min-[200px]/card:text-xs font-bold shadow">
            %{indirimOran}
          </div>
        )}

        {/* Buy Now Badge - Hidden on mobile, shown on hover desktop */}
        <div className="absolute top-1.5 right-1.5 @min-[200px]/card:top-2 @min-[200px]/card:right-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 px-1 py-0.5 @min-[200px]/card:px-1.5 @min-[200px]/card:py-0.5 rounded-md flex items-center gap-0.5 @min-[200px]/card:gap-1 opacity-0 @min-[200px]/card:group-hover:opacity-100 transition-opacity">
          <ShoppingCart className="w-3 h-3 @min-[200px]/card:w-3.5 @min-[200px]/card:h-3.5 text-gray-700 dark:text-gray-300" />
          <span className="text-[10px] @min-[200px]/card:text-xs font-medium text-gray-700 dark:text-gray-300 hidden @min-[200px]/card:inline">
            {t("instantSearch.buyNow")}
          </span>
        </div>

        {/* Favorite Button */}
        <FavoriteButton
          productId={productId}
          className="absolute bottom-1.5 right-1.5 @min-[200px]/card:bottom-2 @min-[200px]/card:right-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-1.5 @min-[200px]/card:p-2 hover:bg-gray-50 dark:hover:bg-gray-700 shadow-sm"
          iconClassName="w-3 h-3 @min-[200px]/card:w-3.5 @min-[200px]/card:h-3.5"
        />
      </div>

      {/* Product Info — tüm kartlarda aynı satır yüksekliği */}
      <div className="flex min-h-0 flex-1 flex-col gap-1 @min-[200px]/card:gap-2 px-0.5 @min-[200px]/card:px-1">
        {/* Store & Rating */}
        <div className="flex min-h-6 shrink-0 items-center justify-between gap-1 @min-[200px]/card:min-h-7 @min-[200px]/card:gap-2">
          {brandName && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                // 🆕 Marka logosuna tıklayınca canonicalUrl'i aç
                if (productUrl) {
                  window.open(productUrl, "_blank", "noopener,noreferrer");
                }
              }}
              className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 px-1 @min-[200px]/card:px-1.5 py-0.5 rounded-md flex items-center gap-0.5 @min-[200px]/card:gap-1 max-w-[55%] @min-[200px]/card:max-w-[60%] hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors flex-shrink-0"
            >
              {brandLogo && (
                <img
                  src={brandLogo}
                  alt={brandName}
                  className="w-2.5 h-2.5 @min-[200px]/card:w-3 @min-[200px]/card:h-3 object-contain flex-shrink-0"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              )}
              <span className="text-[10px] @min-[200px]/card:text-xs font-medium text-gray-700 dark:text-gray-300 truncate">
                {brandName}
              </span>
              <ExternalLink className="w-2.5 h-2.5 @min-[200px]/card:w-3 @min-[200px]/card:h-3 text-gray-700 dark:text-gray-300 flex-shrink-0" />
            </button>
          )}

          {/* Stock badge veya rating */}
          {product.inStock !== undefined && (
            <div
              className={cn(
                "flex items-center gap-0.5 @min-[200px]/card:gap-1 px-1 @min-[200px]/card:px-1.5 py-0.5 rounded-md text-[10px] @min-[200px]/card:text-xs font-medium flex-shrink-0",
                product.inStock
                  ? "bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400"
                  : "bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400"
              )}
            >
              {product.inStock
                ? t("instantSearch.inStock") || "Stokta"
                : t("instantSearch.outOfStock") || "Tükendi"}
            </div>
          )}
        </div>

        {/* Product Name — her zaman 2 satırlık yer */}
        <p className="text-xs @min-[200px]/card:text-sm font-medium text-gray-900 dark:text-gray-100 line-clamp-2 min-h-[1.75rem] @min-[200px]/card:min-h-[2.5rem] leading-tight">
          {productName}
        </p>

        {/* Varyant — yoksa da yeri korunur, fiyatlar hizalı kalır */}
        <p
          data-testid="product-variant"
          className={cn(
            "h-4 shrink-0 truncate text-[10px] font-medium leading-4 @min-[200px]/card:h-5 @min-[200px]/card:text-xs @min-[200px]/card:leading-5",
            variantLabel
              ? "text-gray-500 dark:text-gray-400"
              : "invisible",
          )}
        >
          {variantLabel || "\u00A0"}
        </p>

        {/* Price — alta yaslanır, tek satır */}
        <div className="mt-auto flex min-h-5 shrink-0 items-center gap-1 overflow-hidden @min-[200px]/card:min-h-6 @min-[200px]/card:gap-2">
          <span className="truncate text-xs @min-[200px]/card:text-sm font-semibold text-primary-500 dark:text-primary-400">
            {productPrice}
          </span>
          {productOldPrice && productOldPrice > 0 && (
            <>
              <span className="truncate text-[10px] @min-[200px]/card:text-xs font-normal text-gray-500 dark:text-gray-400 line-through">
                {formatPrice(productOldPrice, userCurrency)}
              </span>
              {indirimOran && (
                <Badge className="shrink-0 bg-primary-50 dark:bg-primary-900/30 border border-primary-100 dark:border-primary-800 text-primary-500 dark:text-primary-400 text-[10px] @min-[200px]/card:text-xs font-medium px-1 @min-[200px]/card:px-1.5 py-0.5">
                  %{indirimOran} off
                </Badge>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );

  // productId varsa gerçek /product/:id sayfasına link ver (Ctrl/Cmd/orta tık yeni
  // sekmede açar); yoksa mevcut fallback olan harici productUrl'i yeni sekmede aç.
  return productId ? (
    <Link
      to={productDetailUrl!}
      onClick={(e) => {
        if (isModifiedClick(e)) return; // Ctrl/Cmd/orta tık → tarayıcı yeni sekmede açsın
        e.preventDefault(); // düz sol tık → eskisi gibi modal aç
        openModal(productId, offerId);
      }}
      className={cardClassName}
    >
      {cardContent}
    </Link>
  ) : (
    <a
      href={productUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={cardClassName}
    >
      {cardContent}
    </a>
  );
}
